import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BottomNav from "@/components/app/BottomNav";
import ImageUploader, { fullUrl } from "@/components/app/ImageUploader";
import { Download, Trash2, LogOut, Shield, Bell, Store } from "lucide-react";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [nickname, setNickname] = useState(user?.nickname || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || null);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!user) return;
    setNickname(user.nickname || "");
    setAvatar(user.avatar_url || null);
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch(()=>{});
    api.post("/notifications/read-all").catch(()=>{});
  }, [user]);

  const save = async () => {
    try {
      const { data } = await api.patch("/me", { nickname, avatar_url: avatar });
      setUser(data);
      toast.success("Perfil atualizado.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateAvatar = async (url) => {
    setAvatar(url);
    try {
      const { data } = await api.patch("/me", { avatar_url: url });
      setUser(data);
      if (url) toast.success("Foto de perfil atualizada!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "encosta-meus-dados.json"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Tem certeza? Essa ação apaga seu perfil, presenças e anonimiza seus eventos.")) return;
    try {
      await api.delete("/me");
      localStorage.removeItem("encosta_token");
      setUser(null);
      toast.success("Conta excluída.");
      nav("/login");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return null;

  return (
    <div className="min-h-[100dvh] pb-20 max-w-md mx-auto">
      <header className="px-6 pt-10 pb-6 flex items-center gap-4">
        <div className="relative">
          <div className="h-20 w-20 rounded-full overflow-hidden bg-muted border-2 border-primary/40 grid place-items-center text-2xl heading font-bold">
            {avatar ? <img src={fullUrl(avatar)} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" /> : (user.nickname?.[0] || "?").toUpperCase()}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl heading font-extrabold truncate">{user.nickname}</h1>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>
      </header>

      <section className="px-6 space-y-4">
        <ImageUploader value={avatar} onChange={updateAvatar} square label="Foto de perfil (opcional · JPG ou PNG)" testid="profile-avatar-uploader" aspect="aspect-square max-w-[180px] mx-auto rounded-full" />
        <p className="text-[11px] text-muted-foreground -mt-2 text-center">Só quem confirmou no mesmo rolê ou jogo e o criador veem sua foto.</p>
        <div className="space-y-2">
          <Label>Apelido</Label>
          <div className="flex gap-2">
            <Input value={nickname} onChange={(e)=>setNickname(e.target.value)} minLength={2} maxLength={30} data-testid="profile-nickname-input" />
            <Button onClick={save} className="press" data-testid="profile-save-button">Salvar</Button>
          </div>
        </div>

        <div className="rounded-xl bg-card border border-white/10 p-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Bell className="w-4 h-4 text-primary" /> Notificações</div>
          {notifs.length === 0 && <p className="text-xs text-muted-foreground">Sem novidades por aqui.</p>}
          <ul className="space-y-2 max-h-56 overflow-y-auto">
            {notifs.map((n) => (
              <li key={n.notification_id} className="text-sm bg-muted/60 rounded-lg px-3 py-2" data-testid={`notif-${n.notification_id}`}>
                <div>{n.message}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString("pt-BR")}</div>
              </li>
            ))}
          </ul>
        </div>

        <Link to="/adega" className="flex items-center gap-3 rounded-xl bg-card border border-primary/30 p-4 press" data-testid="go-to-adega-link">
          <Store className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <div className="font-semibold text-sm">{user.account_type === "adega" ? "Minha adega" : "Sou adega"}</div>
            <div className="text-xs text-muted-foreground">{user.account_type === "adega" ? "Catálogo, horários e dados da loja" : "Cadastre sua loja e apareça no mapa"}</div>
          </div>
        </Link>

        {user.role === "admin" && (
          <Link to="/admin" className="flex items-center gap-3 rounded-xl bg-card border border-white/10 p-4 press" data-testid="go-to-admin-link">
            <Shield className="w-5 h-5 text-primary" />
            <div className="flex-1">
              <div className="font-semibold text-sm">Painel admin</div>
              <div className="text-xs text-muted-foreground">Moderar denúncias</div>
            </div>
          </Link>
        )}

        <div className="rounded-xl bg-card border border-white/10 overflow-hidden divide-y divide-white/5">
          <button onClick={exportData} className="w-full flex items-center gap-3 p-4 press" data-testid="profile-export-button">
            <Download className="w-5 h-5 text-primary" /> <span className="text-sm">Baixar meus dados (JSON)</span>
          </button>
          <button onClick={logout} className="w-full flex items-center gap-3 p-4 press" data-testid="profile-logout-button">
            <LogOut className="w-5 h-5" /> <span className="text-sm">Sair</span>
          </button>
          <button onClick={deleteAccount} className="w-full flex items-center gap-3 p-4 text-destructive press" data-testid="profile-delete-button">
            <Trash2 className="w-5 h-5" /> <span className="text-sm">Excluir minha conta</span>
          </button>
        </div>

        <div className="text-xs text-muted-foreground text-center space-x-2 pt-4">
          <Link to="/termos" className="underline">Termos de Uso</Link>
          <span>·</span>
          <Link to="/privacidade" className="underline">Política de Privacidade</Link>
        </div>
      </section>

      <BottomNav />
    </div>
  );
}
